import { Button, ScrollArea, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useAtom, useSetAtom } from 'jotai';
import { notifications } from '@mantine/notifications';
import { createId } from '@paralleldrive/cuid2';
import type { AuthStateActionType, SpecialLoginType } from '@prisma/client';
import { IconArrowRight, IconComponents, IconCornerDownLeft } from '@tabler/icons-react';
import clsx from 'clsx';
import type { JSX, KeyboardEvent, MouseEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { z } from 'zod';
import { erroredOperationAtom, openCodeDialogOpIdAtom, componentModificationWarningAtom } from '~/atoms/no-code-atoms';
import { useProjectId } from '~/hooks/context/useProjectId';
import { useUpdateStepDescription } from '~/hooks/mutations/steps/useUpdateStepDescription';
import { useTestVariables } from '~/hooks/test/useTestVariables';
import { useLiveStateManager } from '~/hooks/useLiveStateManager';
import { useStartRecorder } from '~/hooks/vm/useStartRecorder';
import { getDummyOperation, spliceInSteps } from '~/logic/optimistic-updates/operation-insert';
import { getDummyStep, spliceInStep } from '~/logic/optimistic-updates/step-insert';
import type {
  OperationData,
  OperationParentContext,
  zAuthStateOp,
  zFetchRequestOp,
  zReceiveEmailOp,
  zSpecialLoginOp,
} from '~/server/logic/steps/operation/types';
import { api } from '~/trpc-extra/api';
import { ExtensionPicker } from '../InstallExtensionDialog';
import ActionSearchBox from './ActionSearchBox';
import type { AddOperationOption } from './AddOperationOption';
import { CUSTOM_ACTIONS_OPTION, OPERATION_OPTIONS } from './AddOperationOption';
import { TestAccountSelect } from './TestAccountSelect';
import { useCopyStepsDialog } from './useCopyStepsDialog';
import { useGetInboxAddress } from './useGetInboxAddress';

const ICON_SIZE = 15;
const CUSTOM_ACTIONS_OPTION_INDEX = -1;

export function AddOperationMenu({
  testContentId,
  onClose,
  closePopover,
  activePageAlias,
  parentStepId,
  parentContexts,
  operationId,
  insertAfterStepOrder,
  insertAfterOperationOrder,
}: {
  testContentId: string;
  onClose?: () => void;
  closePopover?: () => void;
  activePageAlias?: string;
  parentStepId?: string;
  parentContexts?: OperationParentContext[];
  operationId?: string;
  insertAfterStepOrder: number;
  insertAfterStepIndex?: number;
  insertAfterOperationOrder?: number;
}) {
  const parentContext = parentContexts?.at(-1);

  const projectId = useProjectId();
  const trpcUtils = api.useUtils();
  const viewportRef = useRef<HTMLDivElement>(null);

  const { data: testContent } = api.testContent.get.useQuery(
    { id: testContentId },
    { refetchOnMount: false, refetchOnWindowFocus: false, enabled: !!testContentId },
  );
  const testType = testContent?.test.testType;
  const isMobileApp = testType === 'MOBILE_APP';
  const startRecorder = useStartRecorder({ testContentId });
  const setOpenCodeDialogOpId = useSetAtom(openCodeDialogOpIdAtom);
  const setErroredOperation = useSetAtom(erroredOperationAtom);
  const [componentModificationWarning, setComponentModificationWarning] = useAtom(componentModificationWarningAtom);
  const { recorderState, vmStatus, updateLiveState } = useLiveStateManager();

  const [isExecutingAction, setIsExecutingAction] = useState(false);

  const [search, setSearch] = useState('');
  const [isCustomActionsMode, setIsCustomActionsMode] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const { data: components = [] } = api.component.list.useQuery(
    { projectId: projectId || '' },
    { enabled: !!projectId },
  );

  const testVariables = useTestVariables({ testContentId });

  const calculateNextVariableName = () => {
    const existingVarNames = testVariables.map((v) => v.name);
    const varPattern = /^var(\d+)$/;
    const varNumbers = existingVarNames
      .map((name) => {
        const match = name.match(varPattern);
        return match?.[1] ? Number(match[1]) : undefined;
      })
      .filter((x) => x !== undefined);

    const maxVarNumber = varNumbers.length > 0 ? Math.max(...varNumbers) : 0;
    return `var${maxVarNumber + 1}`;
  };

  const calculateNextEmailInboxName = () => {
    const existingVarNames = testVariables.map((v) => v.name);
    const emailPattern = /^emailInbox(\d+)$/;
    const emailNumbers = existingVarNames
      .map((name) => {
        const match = name.match(emailPattern);
        return match?.[1] ? Number(match[1]) : undefined;
      })
      .filter((x) => x !== undefined);

    const maxEmailNumber = emailNumbers.length > 0 ? Math.max(...emailNumbers) : 0;
    return `emailInbox${maxEmailNumber + 1}`;
  };

  const { mutateAsync: addStep } = api.testStep.insert.useMutation({
    async onMutate(newStep) {
      if (newStep.mode === 'AUTH') {
        return;
      }
      const newSelectedStep = getDummyStep(newStep);

      // Cancel outgoing fetches (so they don't overwrite our optimistic update)
      await trpcUtils.testContent.get.cancel({ id: testContentId });

      // Get the data from the queryCache
      const prevTestData = trpcUtils.testContent.get.getData({ id: testContentId });

      trpcUtils.testContent.get.setData({ id: testContentId }, (old) => {
        return old ? { ...old, steps: spliceInStep(old.steps, newSelectedStep) } : old;
      });

      // Return the previous data so we can revert if something goes wrong
      return { prevTestData };
    },
    onError: (err, _newStep, ctx) => {
      notifications.show({ message: 'Error adding a new step', color: 'red' });
      console.error(err);
      // If the mutation fails, use the context-value from onMutate
      if (ctx?.prevTestData) {
        trpcUtils.testContent.get.setData({ id: testContentId }, ctx.prevTestData);
      }
    },
    onSettled: async () => {
      await Promise.allSettled([
        trpcUtils.testContent.get.invalidate({ id: testContentId }),
        trpcUtils.testContent.getCode.invalidate(),
      ]);
    },
  });

  const { updateStepDescription } = useUpdateStepDescription({ testContentId });

  const { mutateAsync: addOperation } = api.operation.upsertOperation.useMutation({
    onSuccess: async (newOperationId) => {
      if (parentContext?.componentId) {
        // Invalidate component query when adding to a component
        void trpcUtils.component.get.invalidate({ id: parentContext.componentId });
      } else if (parentContext?.conditional) {
        // Invalidate conditional block query when adding to a conditional block
        void trpcUtils.operation.getFullConditionalBlock.invalidate({
          conditionalBlockId: parentContext.conditional.conditionalBlockId,
        });
      } else if (parentStepId) {
        await updateStepDescription({ stepId: parentStepId });
      }
      return newOperationId;
    },
    async onMutate(newOperation) {
      if (parentContext) {
        // For component or conditional operations, we'll just invalidate after success for now
        // TODO: Add optimistic update for component operations
        return { prevTestData: undefined };
      }

      // TODO: fix optimistic update
      const newUpsertOperation = getDummyOperation(newOperation);

      // Cancel outgoing fetches (so they don't overwrite our optimistic update)
      await trpcUtils.testContent.get.cancel({ id: testContentId });

      // Get the data from the queryCache
      const prevTestData = trpcUtils.testContent.get.getData({ id: testContentId });

      // TODO: fix optimistic update
      trpcUtils.testContent.get.setData({ id: testContentId }, (old) => {
        return old ? { ...old, steps: spliceInSteps(old.steps, newUpsertOperation) } : old;
      });

      // Return the previous data so we can revert if something goes wrong
      return { prevTestData };
    },
    onError: (err, _newOperation, ctx) => {
      notifications.show({ message: 'Error adding an action', color: 'red' });
      console.error(err);
      // If the mutation fails, use the context-value from onMutate
      if (ctx?.prevTestData) {
        trpcUtils.testContent.get.setData({ id: testContentId }, ctx.prevTestData);
      }
    },
    onSettled: async () => {
      await Promise.allSettled([
        trpcUtils.testContent.get.invalidate({ id: testContentId }),
        trpcUtils.testContent.getCode.invalidate(),
      ]);
    },
  });

  // Helper functions
  const asyncAddActionHandler = <T extends MouseEvent | string | void>(
    asyncAction: (input: T) => void | Promise<void>,
  ) => {
    return async (input: T) => {
      const parentComponentIds =
        parentContexts?.map(({ componentId }) => componentId).filter((id) => id !== undefined) ?? [];

      // Close the popover/dropdown menu before the action has been successfully added to avoid the UI from idling
      closePopover?.();

      // If the action is already executing, do nothing
      // This prevents duplicated clicks
      if (isExecutingAction) {
        return;
      }

      const addAction = async () => {
        // reset component modification warning
        setComponentModificationWarning(undefined);
        // reset error state
        setErroredOperation(undefined);
        try {
          setIsExecutingAction(true);
          await asyncAction(input);
          setSearch('');
        } finally {
          setIsExecutingAction(false);
          onClose?.();
        }
      };

      // Warn the user if adding to a component, modifying the same component will not show a warning
      if (parentComponentIds.length > 0 && componentModificationWarning?.componentId !== parentComponentIds.at(-1)) {
        setComponentModificationWarning({
          componentId: parentComponentIds.at(-1)!,
          rootOperationId: parentContexts?.at(-1)?.rootOperationId,
          currentTestContentId: testContentId,
          onConfirm: addAction,
        });
      } else {
        await addAction();
      }
    };
  };

  const _handleAddOperation = async (operation: OperationData) => {
    if (insertAfterOperationOrder === undefined) {
      throw new Error('Unable to add operation');
    }
    if (!parentStepId && !parentContext) {
      throw new Error('Unable to add operation - no parent');
    }
    return await addOperation({
      operation: {
        ...operation,
        order: insertAfterOperationOrder + 1,
        ...(parentContext ? { parentContext } : { stepId: parentStepId }),
      },
    });
  };

  const addOperationToRightPlaceAndGetId = async ({
    operation,
    // If true, we will add a step instead of an operation
    stepOnly,
    stepDescription,
  }: {
    operation: OperationData;
    stepOnly: boolean;
    stepDescription?: string;
  }) => {
    // Don't need to create a step if we're adding to a component or conditional block
    if (parentContext) {
      return await _handleAddOperation(operation);
    }

    if (!parentStepId || stepOnly) {
      const newStep = await addStep({
        id: createId(),
        description: stepDescription || '',
        order: insertAfterStepOrder + 1,
        testContentId,
        operations: [operation],
      });
      return newStep.operations.at(0)?.id;
    } else {
      return await _handleAddOperation(operation);
    }
  };

  const handleRecordClick = asyncAddActionHandler(async () => {
    // Cancel outgoing fetches (so they don't overwrite our optimistic update)
    await trpcUtils.testContent.get.cancel({ id: testContentId });

    if (parentStepId) {
      const operationOrder = insertAfterOperationOrder === undefined ? 0 : insertAfterOperationOrder + 1;
      if (vmStatus) {
        updateLiveState((prev) => ({
          ...prev,
          vmStatus: {
            state: 'ready',
            humanState: 'ready',
            aliasesToUrls: vmStatus?.aliasesToUrls,
          },
          recorderInitiator: { ...prev.recorderInitiator, executionType: 'record', parentContext },
          recorderCursor: {
            parentContext,
            stepId: parentStepId,
            stepOrder: insertAfterStepOrder,
            operation:
              operationId ?
                {
                  id: operationId,
                  order: operationOrder,
                }
              : undefined,
          },
        }));
      } else {
        startRecorder({
          executionType: 'record',
          location: {
            type: 'operation-id',
            order: insertAfterStepOrder,
            stepId: parentStepId,
            operationId,
            operationOrder,
            parentContexts,
          },
        });
      }
    } else if (recorderState?.executionType === 'record') {
      const newStep = await addStep({
        id: createId(),
        description: '',
        order: insertAfterStepOrder + 1,
        testContentId,
        mode: 'AI_FALLBACK',
      });
      if (newStep.id) {
        try {
          updateLiveState((prev) => ({
            ...prev,
            recorderCursor: { stepId: newStep.id, stepOrder: newStep.stepOrder, parentContext },
          }));
        } catch (e) {
          console.debug('Error moving recorder cursor:', e);
        }
      }
    } else {
      const dummyStep = getDummyStep({
        description: '',
        order: insertAfterStepOrder + 1,
        testContentId,
        mode: 'AI_FALLBACK',
      });

      startRecorder({
        executionType: 'record',
        location: { type: 'insert-step', order: insertAfterStepOrder + 1, stepId: dummyStep.id },
      });

      trpcUtils.testContent.get.setData({ id: testContentId }, (old) => {
        return old ? { ...old, steps: spliceInStep(old.steps, dummyStep) } : old;
      });
    }
  });

  const handleAddAIAssertionStep = asyncAddActionHandler(async () => {
    const operation: OperationData = {
      operationId: createId(),
      order: 0,
      pageAlias: activePageAlias || 'page',
      operation: {
        operationType: 'AI_ASSERTION' as const,
        assertion: '',
        screenshotType: 'VIEWPORT',
        model: 'STABLY_FLASH',
      },
    };

    await addOperationToRightPlaceAndGetId({ operation, stepOnly: false, stepDescription: 'AI Assertion' });
  });

  const handleAddAIOperation = asyncAddActionHandler(async () => {
    const operation: OperationData = {
      operationId: createId(),
      order: 0,
      pageAlias: activePageAlias || 'page',
      operation: {
        operationType: 'AI_ACTION' as const,
        prompt: '',
        model: 'STABLY_FLASH' as const,
      },
    };

    await addOperationToRightPlaceAndGetId({ operation, stepOnly: false, stepDescription: 'AI Action' });
  });

  const handleAddAiStep = asyncAddActionHandler(async (prompt?: string) => {
    await addStep({
      id: createId(),
      description: prompt || '',
      order: insertAfterStepOrder + 1,
      testContentId,
      mode: 'AI_DYNAMIC',
    });
  });

  const handleCodeEditClick = asyncAddActionHandler(async () => {
    const operation: OperationData = {
      operationId: createId(),
      order: 0,
      pageAlias: activePageAlias || 'page',
      operation: { operationType: 'CODE_BLOCK' as const, code: '' },
    };

    const opId = await addOperationToRightPlaceAndGetId({
      operation,
      stepOnly: false,
      stepDescription: 'Custom Code',
    });
    setOpenCodeDialogOpId(opId);
  });

  const handleAddEmptyStep = asyncAddActionHandler(async () => {
    const newStep = await addStep({
      id: createId(),
      description: '',
      order: insertAfterStepOrder + 1,
      mode: 'AI_FALLBACK',
      testContentId,
    });
    if (vmStatus) {
      updateLiveState((prev) => ({
        ...prev,
        recorderCursor: { stepId: newStep.id, stepOrder: newStep.stepOrder },
      }));
    }
  });

  const handleAddNavigateStep = asyncAddActionHandler(async () => {
    const operation: OperationData = {
      operationId: createId(),
      order: 0,
      pageAlias: activePageAlias || 'page',
      operation: {
        operationType: 'NAVIGATION' as const,
        value: 'https://',
        valueType: 'STATIC' as const,
      },
    };
    await addOperationToRightPlaceAndGetId({
      operation,
      stepOnly: false,
      stepDescription: 'Navigate to https://',
    });
  });

  const handleAddWaitStep = asyncAddActionHandler(async () => {
    const operation: OperationData = {
      operationId: createId(),
      order: 0,
      pageAlias: activePageAlias || 'page',
      operation: {
        operationType: 'WAIT' as const,
        timeoutInMillis: 3000,
      },
    };
    await addOperationToRightPlaceAndGetId({
      operation,
      stepOnly: false,
      stepDescription: 'Wait for 3 seconds',
    });
  });

  const handleAddRefreshStep = asyncAddActionHandler(async () => {
    const operation: OperationData = {
      operationId: createId(),
      order: 0,
      pageAlias: activePageAlias || 'page',
      operation: {
        operationType: 'ACTION' as const,
        action: 'reload' as const,
        valueType: 'STATIC' as const,
      },
    };
    await addOperationToRightPlaceAndGetId({
      operation,
      stepOnly: false,
      stepDescription: 'Refresh the page',
    });
  });

  const handleAddScrollStep = asyncAddActionHandler(async () => {
    const operation: OperationData = {
      operationId: createId(),
      order: 0,
      pageAlias: activePageAlias || 'page',
      operation: {
        operationType: 'ACTION' as const,
        action: 'scroll' as const,
        value: JSON.stringify({ direction: 'down', amount: 100 }),
        valueType: 'STATIC' as const,
      },
    };
    await addOperationToRightPlaceAndGetId({
      operation,
      stepOnly: false,
      stepDescription: 'Scroll down 100 pixels',
    });
  });

  const handleAddVariableStep = asyncAddActionHandler(async () => {
    const operation: OperationData = {
      operationId: createId(),
      order: 0,
      pageAlias: activePageAlias || 'page',
      operation: {
        operationType: 'DEFINE_VARIABLE' as const,
        name: calculateNextVariableName(),
        type: 'STATIC' as const,
        value: 'temp value',
      },
    };
    await addOperationToRightPlaceAndGetId({
      operation,
      stepOnly: false,
      stepDescription: 'Define a variable',
    });
  });

  // this is a shortcut for the user to add a DEFINE_VARIABLE operation preconfigured for AI extraction (visual content)
  const handleAddExtractVisualStep = asyncAddActionHandler(async () => {
    const operation: OperationData = {
      operationId: createId(),
      order: 0,
      pageAlias: activePageAlias || 'page',
      operation: {
        operationType: 'DEFINE_VARIABLE' as const,
        name: calculateNextVariableName(),
        type: 'AI_EXTRACT_PROMPT' as const,
        value: 'temp value',
        includeScreenshot: true,
      },
    };
    await addOperationToRightPlaceAndGetId({
      operation,
      stepOnly: false,
      stepDescription: 'Extract visual content from the page with AI',
    });
  });

  const { getInboxAddressStatic } = useGetInboxAddress();

  const handleAddAddEmailInboxStep = asyncAddActionHandler(async () => {
    const operation: OperationData = {
      operationId: createId(),
      order: 0,
      pageAlias: activePageAlias || 'page',
      operation: {
        operationType: 'DEFINE_VARIABLE' as const,
        name: calculateNextEmailInboxName(),
        value: getInboxAddressStatic('MUST_MODIFY'),
        type: 'STATIC' as const,
      },
    };
    await addOperationToRightPlaceAndGetId({
      operation,
      stepOnly: false,
      stepDescription: 'Add an email inbox',
    });
  });

  const handleAddReceiveEmailStep = asyncAddActionHandler(async () => {
    const operation: OperationData = {
      operationId: createId(),
      order: 0,
      pageAlias: activePageAlias || 'page',
      operation: {
        operationType: 'RECEIVE_EMAIL' as const,
        toEmailAddressValue: '',
        toEmailAddressType: 'STATIC',
        filters: {
          sender: '',
          subject: '',
        },
      } satisfies z.output<typeof zReceiveEmailOp>,
    };
    await addOperationToRightPlaceAndGetId({
      operation,
      stepOnly: false,
      stepDescription: 'Extract content from the latest email',
    });
  });

  const handleAddFetchRequestStep = asyncAddActionHandler(async () => {
    const operation: OperationData = {
      operationId: createId(),
      order: 0,
      pageAlias: activePageAlias || 'page',
      operation: {
        operationType: 'FETCH_REQUEST' as const,
        responseVariableName: '',
        request: {
          url: 'https://',
          method: 'GET',
          headers: {},
          body: '',
        },
      } satisfies z.output<typeof zFetchRequestOp>,
    };
    await addOperationToRightPlaceAndGetId({
      operation,
      stepOnly: false,
      stepDescription: 'Execute a fetch/HTTP request to a URL',
    });
  });

  const handleAddComponentCallStep = asyncAddActionHandler(async (componentId: string) => {
    // Direct component call with specific component ID
    const selectedComponent = components.find((c) => c.id === componentId);
    const componentName = selectedComponent?.name || 'component';

    // Create default input parameters (empty object since no parameters configured)
    const inputParameters: Record<string, { type: 'STATIC' | 'DYNAMIC'; value: string }> = {};

    const operation: OperationData = {
      operationId: createId(),
      order: 0,
      pageAlias: activePageAlias || 'page',
      operation: {
        operationType: 'COMPONENT_CALL' as const,
        componentId,
        componentName,
        inputParameters,
      },
    };

    await addOperationToRightPlaceAndGetId({
      operation,
      stepOnly: false,
      stepDescription: `Call ${componentName}`,
    });
  });

  const handleAddConditionalStep = asyncAddActionHandler(async () => {
    const operation: OperationData = {
      operationId: createId(),
      order: 0,
      pageAlias: activePageAlias || 'page',
      operation: {
        operationType: 'CONDITIONAL' as const,
        conditionPrompt: '',
        conditionalBlockId: '',
      },
    };
    await addOperationToRightPlaceAndGetId({
      operation,
      stepOnly: false,
      stepDescription: 'Execute different operations based on a condition',
    });
  });

  const handleAddEndTestStep = asyncAddActionHandler(async () => {
    const operation: OperationData = {
      operationId: createId(),
      order: 0,
      pageAlias: activePageAlias || 'page',
      operation: {
        operationType: 'END_TEST' as const,
        passTest: true,
      },
    };
    await addOperationToRightPlaceAndGetId({
      operation,
      stepOnly: false,
      stepDescription: 'End test execution successfully',
    });
  });

  const handleAddAuthStateStep = asyncAddActionHandler(async (actionType: AuthStateActionType) => {
    modals.open({
      withCloseButton: false,
      size: 'sm',
      children: (
        <TestAccountSelect
          onSubmit={async (testAccountId: string) => {
            const stepDescriptions = {
              LOAD: 'Load authenticated state from a Test Account',
              STORE: 'Store authenticated state to a Test Account',
              CLEAR: 'Clear authenticated state for a Test Account',
            };

            const operation: OperationData = {
              operationId: createId(),
              order: 0,
              pageAlias: activePageAlias || 'page',
              operation: {
                operationType: 'AUTH_STATE' as const,
                actionType,
                testAccountId,
              } satisfies z.output<typeof zAuthStateOp>,
            };

            console.log('New Auth State Operation: ', operation);
            await addOperationToRightPlaceAndGetId({
              operation,
              stepOnly: false,
              stepDescription: stepDescriptions[actionType],
            });
          }}
        />
      ),
    });
  });

  const handleAddSpecialLoginStep = asyncAddActionHandler(async (loginType: SpecialLoginType) => {
    modals.open({
      withCloseButton: false,
      size: 'sm',
      children: (
        <TestAccountSelect
          filter={(testAccount) => testAccount.type === 'GOOGLE'}
          accountType="GOOGLE"
          onSubmit={async (testAccountId: string) => {
            const operation: OperationData = {
              operationId: createId(),
              order: 0,
              pageAlias: activePageAlias || 'page',
              operation: {
                operationType: 'SPECIAL_LOGIN' as const,
                type: loginType,
                testAccountId,
              } satisfies z.output<typeof zSpecialLoginOp>,
            };

            await addOperationToRightPlaceAndGetId({
              operation,
              stepOnly: false,
              stepDescription: `Log in to ${loginType} with a test account`,
            });
          }}
        />
      ),
    });
  });

  const handleAddExtension = () => {
    modals.open({
      title: 'Install Extension',
      children: <ExtensionPicker testContentId={testContentId} />,
    });
    closePopover?.();
  };

  const { openCopyStepsDialog } = useCopyStepsDialog();
  const handleCopySteps = asyncAddActionHandler(async () => {
    openCopyStepsDialog({
      insertAfterStepOrder,
      copyIntoTestContentId: testContentId,
    });
  });

  const { filteredOptions, showAIFirst, showNoResults } = useMemo(() => {
    if (isCustomActionsMode) {
      return { filteredOptions: [] as AddOperationOption[], showAIFirst: false, showNoResults: false };
    }

    const searchLower = search.toLowerCase().trim();
    // Not including custom actions. We will add it separately since we want to always show it at the top

    const stepFilteredOptions = OPERATION_OPTIONS.filter(
      (option) =>
        option.value !== 'custom' &&
        (option.label.toLowerCase().includes(searchLower) ||
          option.description?.toLowerCase().includes(searchLower) ||
          option.searchTerms?.some((term) => term.toLowerCase().includes(searchLower)) ||
          (searchLower === 'log in' && option.group === 'Log in with account')),
    );

    // Component filtering logic
    // 1. If there are matches, show the options with search filtering
    // 2. If there are no matches, show all component options
    const createComponentOptions = ({
      includeSearchFilter,
    }: {
      includeSearchFilter: boolean;
    }): AddOperationOption[] => {
      return components
        .filter((component) => !component.archived)
        .filter((component) => !parentContexts?.some((context) => context.componentId === component.id))
        .filter((component) => {
          if (!includeSearchFilter) {
            return true;
          }
          const searchFilteredLower = search.toLowerCase().trim();
          return (
            searchFilteredLower === '' ||
            component.name.toLowerCase().includes(searchFilteredLower) ||
            'component'.includes(searchFilteredLower) ||
            'components'.includes(searchFilteredLower)
          );
        })
        .map((component) => ({
          value: `component:${component.id}`,
          label: component.name,
          icon: <IconComponents size={ICON_SIZE} />,
          description: `${component.operations?.length || 0} actions${
            component.parameters && component.parameters.length > 0 ? `, ${component.parameters.length} parameters` : ''
          }`,
          group: 'Components',
        }));
    };

    // Transform components into options with search filtering
    const componentOptions = createComponentOptions({ includeSearchFilter: true });

    if (searchLower && stepFilteredOptions.length === 0 && componentOptions.length === 0) {
      // When no matches, show all options with AI first
      const allStepOptions = OPERATION_OPTIONS.filter((option) => option.value !== 'custom');
      const allComponentOptions = createComponentOptions({ includeSearchFilter: false });

      const allRecordIndex = allStepOptions.findIndex((opt) => opt.value === 'record');
      const allRecordOption = allRecordIndex !== -1 ? allStepOptions[allRecordIndex] : null;
      const allOtherStepOptions = allStepOptions.filter((opt) => opt.value !== 'record');

      return {
        filteredOptions: [
          ...(allRecordOption ? [allRecordOption] : []),
          ...allOtherStepOptions,
          ...allComponentOptions,
        ],
        showAIFirst: !parentStepId, // Show AI option first when no matches and not in a step
        showNoResults: true,
      };
    }

    const recordIndex = stepFilteredOptions.findIndex((opt) => opt.value === 'record');
    const recordOption = recordIndex !== -1 ? stepFilteredOptions[recordIndex] : null;
    const otherStepOptions = stepFilteredOptions.filter((opt) => opt.value !== 'record');

    return {
      filteredOptions: [
        ...(recordOption ? [recordOption] : []),
        // Do not show the custom actions option if adding an operation inside of a step
        ...(parentStepId ? [] : [CUSTOM_ACTIONS_OPTION]),
        ...otherStepOptions,
        ...componentOptions,
      ],
      showAIFirst: false,
      showNoResults: false,
    };
  }, [isCustomActionsMode, search, isMobileApp, components, parentStepId]);

  const handleOptionSelect = async (value: string) => {
    if (value === 'custom') {
      setIsCustomActionsMode(true);
      textareaRef.current?.focus();
      return;
    }
    if (value.startsWith('component:')) {
      const [, componentId] = value.split(':');
      await handleAddComponentCallStep(componentId);
    } else {
      // oxlint-disable-next-line no-restricted-syntax
      switch (value) {
        case 'ai-assertion':
          await handleAddAIAssertionStep();
          break;
        case 'ai-operation':
          await handleAddAIOperation();
          break;
        case 'code':
          await handleCodeEditClick();
          break;
        case 'empty':
          await handleAddEmptyStep();
          break;
        case 'navigate':
          await handleAddNavigateStep();
          break;
        case 'record':
          await handleRecordClick();
          break;
        case 'wait':
          await handleAddWaitStep();
          break;
        case 'refresh':
          await handleAddRefreshStep();
          break;
        case 'scroll':
          await handleAddScrollStep();
          break;
        case 'variable':
          await handleAddVariableStep();
          break;
        case 'extract-visual':
          await handleAddExtractVisualStep();
          break;
        case 'setup-email-inbox':
          await handleAddAddEmailInboxStep();
          break;
        case 'receive-email':
          await handleAddReceiveEmailStep();
          break;
        case 'fetch-request':
          await handleAddFetchRequestStep();
          break;
        case 'copy-steps':
          await handleCopySteps();
          break;
        case 'add-extension':
          handleAddExtension();
          break;
        case 'load-auth-state':
          await handleAddAuthStateStep('LOAD');
          break;
        case 'store-auth-state':
          await handleAddAuthStateStep('STORE');
          break;
        case 'clear-auth-state':
          await handleAddAuthStateStep('CLEAR');
          break;
        case 'special-login-google':
          await handleAddSpecialLoginStep('GOOGLE');
          break;
        case 'conditional':
          await handleAddConditionalStep();
          break;
        case 'end-test':
          await handleAddEndTestStep();
          break;
        default:
          break;
      }
    }
  };

  const handleSubmit = async () => {
    if (isExecutingAction) {
      return;
    }
    setIsExecutingAction(true);
    try {
      if (isCustomActionsMode) {
        await handleAddAiStep(search);
      } else if (showAIFirst && selectedIndex === CUSTOM_ACTIONS_OPTION_INDEX) {
        // Custom AI option at top is selected
        await handleAddAiStep(search.trim());
      } else if (filteredOptions && filteredOptions.length > 0) {
        await handleOptionSelect(filteredOptions[selectedIndex]?.value || '');
      }
    } finally {
      setIsExecutingAction(false);
    }
  };

  const handleKeyDown = async (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      await handleSubmit();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, (filteredOptions?.length ?? 0) - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    }
  };

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // const handleEnhanceDescription = async () => {
  //   setIsEnhancing(true);
  //   try {
  //     if (!hasEnhanced) {
  //       setOriginalSearch(search);
  //     }
  //     const enhancedDescription = await enhanceNewStepDescription.mutateAsync({ description: search });
  //     setSearch(enhancedDescription);
  //     setHasEnhanced(true);
  //   } catch (error) {
  //     console.error('Error enhancing description:', error);
  //     notifications.show({ message: 'Failed to enhance description', color: 'red' });
  //   } finally {
  //     setIsEnhancing(false);
  //   }
  // };

  // const handleUndoEnhance = () => {
  //   setSearch(originalSearch);
  //   setHasEnhanced(false);
  // };

  useEffect(() => {
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      // Only handle if we're in custom mode and search is empty
      if (isCustomActionsMode && search === '') {
        if (event.key === 'Backspace' || event.key === 'Escape') {
          setIsCustomActionsMode(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isCustomActionsMode, search]);

  useEffect(() => {
    // Only allow AI Generate mode when adding new steps (not when adding operations within a step)
    if (search && (filteredOptions || []).length === 0 && !parentStepId) {
      setIsCustomActionsMode(true);
    }
  }, [search, filteredOptions, parentStepId]);

  useEffect(() => {
    if (!search) {
      setSelectedIndex(0);
      return;
    }
    if (showAIFirst) {
      setSelectedIndex(CUSTOM_ACTIONS_OPTION_INDEX);
    } else if ((filteredOptions || []).length > 1) {
      setSelectedIndex(1);
    } else {
      setSelectedIndex(0);
    }
  }, [search, filteredOptions, showAIFirst, setSelectedIndex]);

  // Requires both of these to be passed in if parentStepId is passed in
  if (parentStepId && insertAfterOperationOrder === undefined) {
    return <div>Unable to render the options because of missing data</div>;
  }

  return (
    <div>
      <ActionSearchBox
        className="px-4"
        textareaRef={textareaRef}
        handleKeyDown={handleKeyDown}
        isCustomActionsMode={isCustomActionsMode}
        search={search}
        setSearch={setSearch}
      />
      {!isCustomActionsMode && (
        <ScrollArea.Autosize
          className="mt-2 overflow-y-auto"
          viewportRef={viewportRef}
          // The scroll area height is adjusted based on the viewport height (40%) so that the options are always visible, and don't get cut off by the top or bottom of short screens.
          mah="min(465px, calc(40vh))"
          type="always"
          scrollbars="y"
        >
          <div
            className={clsx('flex h-full min-h-20 flex-col px-4', {
              'justify-end': isCustomActionsMode,
            })}
          >
            {!!showAIFirst && (
              <>
                <MenuButton
                  disabled={isExecutingAction}
                  onClick={() => handleAddAiStep(search.trim())}
                  selected={selectedIndex === CUSTOM_ACTIONS_OPTION_INDEX}
                >
                  <div className="flex w-full items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-shrink-0">{CUSTOM_ACTIONS_OPTION.icon}</div>
                      <div className="ml-2">
                        <span className="flex-grow">Add with AI: "{search}"</span>
                        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          AI will convert your description into test steps
                        </div>
                      </div>
                    </div>
                    <IconCornerDownLeft
                      size={ICON_SIZE}
                      className={`ml-2 flex-shrink-0 ${selectedIndex === CUSTOM_ACTIONS_OPTION_INDEX ? '' : 'invisible'}`}
                    />
                  </div>
                </MenuButton>
                <div className="border-t border-gray-200 dark:border-gray-700" />
              </>
            )}
            {(!!showNoResults || !!showAIFirst) && (
              <div className="my-2 text-center">
                <Text
                  size="sm"
                  c="dimmed"
                  fs="italic"
                >
                  No results found for "{search}".
                </Text>
              </div>
            )}
            {filteredOptions.reduce((acc: JSX.Element[], option, index, array) => {
              if (!(isMobileApp && option.isWebOnly)) {
                if (index === 0 || option.group !== array[index - 1]?.group) {
                  if (index !== 0) {
                    acc.push(
                      <div
                        key={`divider-${option.group || 'default'}`}
                        className="mt-2 border-t border-gray-200 py-1 dark:border-gray-700"
                      />,
                    );
                  }
                  if (option.group) {
                    acc.push(
                      <div
                        key={option.group}
                        className="my-2 text-xs font-semibold text-gray-500"
                      >
                        {option.group}
                      </div>,
                    );
                  }
                }
                acc.push(
                  <MenuButton
                    disabled={isExecutingAction}
                    key={option.value}
                    onClick={() => handleOptionSelect(option.value)}
                    selected={selectedIndex === index}
                    scrollRef={selectedIndex === index ? (el) => el?.scrollIntoView({ block: 'nearest' }) : undefined}
                  >
                    <div className="flex w-full items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className="flex-shrink-0">{option.icon}</div>
                        <div className="ml-2">
                          <span className="flex-grow">{option.label}</span>
                          {option.description ?
                            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{option.description}</div>
                          : null}
                        </div>
                      </div>
                      <IconCornerDownLeft
                        size={ICON_SIZE}
                        className={`ml-2 flex-shrink-0 ${selectedIndex === index ? '' : 'invisible'}`}
                      />
                    </div>
                  </MenuButton>,
                );
              }

              return acc;
            }, [])}
          </div>
        </ScrollArea.Autosize>
      )}
      {!!isCustomActionsMode && (
        <div className="mt-2 flex min-h-20 flex-col justify-end">
          <div className="flex h-full justify-end gap-2 px-4">
            <Button
              onClick={() => {
                setIsCustomActionsMode(false);
                setSearch('');
                textareaRef.current?.focus();
              }}
              disabled={isExecutingAction}
              size="compact-sm"
              variant="light"
            >
              Back
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isExecutingAction}
              size="compact-sm"
              variant="filled"
              rightSection={<IconArrowRight size={14} />}
            >
              Enter
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

const MenuButton = ({
  onClick,
  disabled = false,
  selected = false,
  children,
  scrollRef,
}: {
  onClick: () => void;
  disabled?: boolean;
  selected?: boolean;
  children: React.ReactNode;
  scrollRef?: (el: HTMLButtonElement | null) => void;
}) => {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      ref={scrollRef}
      className={clsx(
        'my-1 flex w-full cursor-pointer flex-col items-start justify-start rounded-sm p-2 py-1 text-left text-[14px] text-stone-800 enabled:hover:bg-gray-100 disabled:opacity-30 disabled:hover:cursor-not-allowed dark:text-stone-200 enabled:dark:hover:bg-gray-700',
        {
          'rounded-md bg-blue-100 dark:bg-slate-700': selected,
        },
      )}
    >
      {children}
    </button>
  );
};
